import React, { useState } from 'react';
import { Stack, Box, TextField, IconButton, CircularProgress } from '@mui/material';
import Iconify from 'src/components/iconify';
import { resizeSignatureImage, uploadPrivateImageFormData } from 'src/services/image.service';
import { getSecureViewUrl } from 'src/utils/secure-files';

export default function RHFSignatureUpload({
                                             initialImage = null,
                                             fileName = 'signature',
                                             folderName = 'signatures',
                                             userId,
                                             name,
                                             setValue,
                                             width = 400,
                                             height = 150,
                                             label = 'Doctor Signature',
                                             maxSizeMB = 2,
                                           }) {
  const [image, setImage] = useState(initialImage ? [initialImage] : []);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [displayImage, setDisplayImage] = useState(null);

  React.useEffect(() => {
    if (initialImage && initialImage !== image[0]) {
      setImage([initialImage]);
    } else if (!initialImage && image.length > 0 && !preview) {
      setImage([]);
    }
  }, [initialImage, image, preview]);

  React.useEffect(() => {
    let isMounted = true;
    const loadImg = async () => {
      const currentVal = preview || image[0];
      if (currentVal) {
        if (currentVal.startsWith('blob:')) {
          if (isMounted) setDisplayImage(currentVal);
        } else {
          const url = await getSecureViewUrl(currentVal);
          if (isMounted && url) setDisplayImage(url);
        }
      } else if (isMounted) {
        setDisplayImage(null);
      }
    };
    loadImg();
    return () => { isMounted = false; };
  }, [image, preview]);

  const validateFile = (file) => {
    const validTypes = ['image/png', 'image/jpeg', 'image/jpg'];

    if (!validTypes.includes(file.type)) {
      throw new Error('Only PNG or JPG allowed');
    }

    if (file.size > maxSizeMB * 1024 * 1024) {
      throw new Error(`File must be under ${maxSizeMB}MB`);
    }
  };

  const validateDimensions = (file) =>
    new Promise((resolve, reject) => {
      const img = new Image();
      const url = URL.createObjectURL(file);

      img.onload = () => {
        URL.revokeObjectURL(url);

        if (img.width < 200 || img.height < 80) {
          reject(new Error('Signature image too small'));
          return;
        }

        resolve(true);
      };

      img.onerror = () => reject(new Error('Invalid image file'));
      img.src = url;
    });

  const handleUpload = async (file) => {
    try {
      if (!userId) throw new Error('User ID is required for upload');

      validateFile(file);
      await validateDimensions(file);

      const localPreview = URL.createObjectURL(file);
      setPreview(localPreview);
      setUploading(true);

      const resizedFile = await resizeSignatureImage(file, width, height);
      console.log('Original size:', (file.size / 1024).toFixed(2), 'KB');
      console.log('Resized size:', (resizedFile.size / 1024).toFixed(2), 'KB');
      const formData = new FormData();
      formData.append('file', resizedFile);

      const extension = file.type.split('/')[1];

      // ✅ NEW SAFE FILENAME
      const filename = `${userId}/${Date.now()}_${fileName}.${extension}`;

      const res = await uploadPrivateImageFormData(formData, filename, folderName);

      const key = res.key || res.data?.key || res.url; // Fallback to url if key isn't provided

      setImage([key]);
      setValue(name, key);

      setPreview(null);
      setUploading(false);
    } catch (err) {
      setUploading(false);
      setPreview(null);
      alert(err.message || 'Upload failed');
    }
  };

  const handleChange = (e) => {
    const file = e.target.files?.[0];
    if (file) handleUpload(file);
  };

  const handleRemove = () => {
    setImage([]);
    setPreview(null);
    setValue(name, null);
  };

  return (
    <Stack spacing={2}>
      <Stack sx={{ position: 'relative' }}>
        <TextField
          label={label}
          placeholder="Upload signature"
          InputProps={{
            endAdornment: <Iconify icon="tabler:signature" />,
          }}
        />

        <input
          type="file"
          accept="image/png, image/jpeg"
          onChange={handleChange}
          style={{
            opacity: 0,
            position: 'absolute',
            width: '100%',
            height: '100%',
            cursor: 'pointer',
          }}
        />
      </Stack>

      {displayImage && (
        <Box
          sx={{
            width,
            height,
            border: '1px solid #ccc',
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            backgroundColor: '#fafafa',
          }}
        >
          <img
            src={displayImage}
            alt="signature"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              opacity: uploading ? 0.5 : 1,
            }}
          />

          {uploading && (
            <CircularProgress
              size={28}
              sx={{ position: 'absolute' }}
            />
          )}

          <IconButton
            onClick={handleRemove}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              backgroundColor: 'white',
            }}
          >
            <Iconify icon="tabler:x" />
          </IconButton>
        </Box>
      )}
    </Stack>
  );
}
